// T2-H.1 — fail-loud taxonomy for the content-pack → EngineItem mapper.
//
// Before T2-H.1, contentItemToEngineItem's `default` branch silently
// emitted a degenerate multiple_choice item (options: undefined, empty
// prompt/answer) for ANY pack without an explicit mapper. That item is
// unrenderable — a kid would see a blank question. T2-H.1 replaces the
// default with an EXHAUSTIVE switch that throws one of the classes below,
// so an unmapped / mis-routed pack fails LOUD (the session refuses to
// start with a traceable error) instead of degrading silently.
//
// FIVE classes. The fifth — MapperShapeMismatchError — landed in S5.3 (the
// first real guarded mappers: comprehensionQuestions / semanticRelations /
// grammarItems). It guards a mapper's OWN output invariants (a mapped item
// that isn't renderable — empty prompt/answer, <2 options, answer not in
// options, unknown ageBand) so a content/mapper bug fails LOUD at map time
// rather than rendering a broken question. The invariant identifiers live in
// INVARIANTS (below); assertMapperInvariants (contentToEngineItem.ts) runs the
// checks as the last line of each guarded mapper.
//
//   UnknownPackError        — pack string is not a ContentPackName at all
//                             (belt-and-suspenders behind the compile-time
//                             exhaustiveness check; fires only if a caller
//                             bypasses the type with a raw string).
//   NotKidFacingPackError   — a real ContentPackName, but one that never
//                             serves kid-facing items (feedbackStrings,
//                             scaffoldPrompts, parentSummaryTemplates are
//                             consumed via other paths). Reaching the mapper
//                             with one is a routing bug.
//   NoMapperImplementedError— a real ContentPackName, kid-facing in scope,
//                             whose mapper hasn't shipped yet. Carries the
//                             planned sub-PR so the operator knows where the
//                             fix lives.
//   NoAuthoredContentError  — a placeholder pack with zero authored items
//                             (categories, attributes, multipleMeaningWords,
//                             tier2Vocabulary). Surfaces the real gap:
//                             content, not code.

import type { ContentPackName } from "@/curriculum/types/content";

/** Structured context attached to every mapper error, for traceable logs.
 *  The mapper only has the pack, the item, and the skill id in scope, so
 *  that's what we thread (caller/audience live a frame up and can be added
 *  by the catch site if needed). */
export interface MapperErrorContext {
  /** The (canonical) skill id the item pull was for. */
  skillId?: string;
  /** The offending content item's id, when present on the item. */
  itemId?: string;
}

function ctxSuffix(ctx: MapperErrorContext): string {
  const parts: string[] = [];
  if (ctx.skillId) parts.push(`skill=${ctx.skillId}`);
  if (ctx.itemId) parts.push(`item=${ctx.itemId}`);
  return parts.length ? ` (${parts.join(", ")})` : "";
}

/** Base class so a single `instanceof MapperError` catches the whole family. */
export class MapperError extends Error {
  readonly pack: string;
  readonly context: MapperErrorContext;
  constructor(name: string, message: string, pack: string, context: MapperErrorContext) {
    super(message);
    this.name = name;
    this.pack = pack;
    this.context = context;
  }
}

export class UnknownPackError extends MapperError {
  constructor(pack: string, context: MapperErrorContext = {}) {
    super("UnknownPackError", `Unknown pack: ${pack}${ctxSuffix(context)}`, pack, context);
  }
}

export class NotKidFacingPackError extends MapperError {
  constructor(pack: ContentPackName, context: MapperErrorContext = {}) {
    super(
      "NotKidFacingPackError",
      `Pack ${pack} is not kid-facing — should not reach contentItemToEngineItem${ctxSuffix(context)}`,
      pack,
      context,
    );
  }
}

export class NoMapperImplementedError extends MapperError {
  /** The sub-PR that will ship this pack's mapper (e.g. "T2-H.3"). */
  readonly plannedSubPR: string;
  constructor(pack: ContentPackName, plannedSubPR: string, context: MapperErrorContext = {}) {
    super(
      "NoMapperImplementedError",
      `No mapper for ${pack} yet (planned: ${plannedSubPR})${ctxSuffix(context)}`,
      pack,
      context,
    );
    this.plannedSubPR = plannedSubPR;
  }
}

export class NoAuthoredContentError extends MapperError {
  constructor(pack: ContentPackName, context: MapperErrorContext = {}) {
    super(
      "NoAuthoredContentError",
      `Pack ${pack} has no authored content (placeholder)${ctxSuffix(context)}`,
      pack,
      context,
    );
  }
}

/** Invariant identifiers a guarded mapper's output must satisfy. Tests pin
 *  these strings. Renderability contract for a mapped EngineItem: the option
 *  checks apply only to choice items (type === "multiple_choice").
 *
 *  NB: we use OPTIONS_MIN_2 + OPTIONS_INCLUDE_ANSWER + OPTIONS_DISTINCT rather
 *  than the Wave-C-audit-sketched OPTIONS_LENGTH_4. Every current
 *  comprehension/semantic/grammar item happens to carry exactly 4 options, but
 *  "≥2 distinct, answer among them" is the actual renderability requirement
 *  (and matches the controlledSentences/foundationItems schema invariants);
 *  hardcoding 4 would falsely reject a future valid 3- or 5-option item. */
export const INVARIANTS = {
  ID_NON_EMPTY: "id_non_empty",
  PROMPT_NON_EMPTY: "prompt_non_empty",
  ANSWER_NON_EMPTY: "answer_non_empty",
  OPTIONS_ARRAY: "options_is_array",
  OPTIONS_MIN_2: "options_min_2",
  OPTIONS_INCLUDE_ANSWER: "options_include_answer",
  OPTIONS_DISTINCT: "options_distinct",
  MICROSKILL_PRESENT: "microskill_present",
  AGEBAND_PRESENT: "ageband_present",
  AGEBAND_VALID: "ageband_in_known_set",
  // MA-616a — an authored decodablePassages `clozeSlot.word` (see
  // docs/build/specs/MA-616-dp-cloze-slot.md) must name a real token core of
  // the passage's last sentence. This is unreachable on committed content
  // (dpClozeSlotGuard.mjs enforces it as a standing audit), but the mapper
  // fails loud rather than silently falling back to the old last-word
  // behaviour if it is ever violated.
  CLOZE_SLOT_WORD_MATCHED: "cloze_slot_word_matched",
} as const;

export type MapperInvariant = (typeof INVARIANTS)[keyof typeof INVARIANTS];

/** A mapper produced an item that violates a renderability invariant. The
 *  failed invariant id is carried for traceable logs; itemId rides in context
 *  (the shared MapperErrorContext), matching the rest of the taxonomy. */
export class MapperShapeMismatchError extends MapperError {
  readonly invariant: MapperInvariant;
  constructor(pack: ContentPackName, invariant: MapperInvariant, context: MapperErrorContext = {}) {
    super(
      "MapperShapeMismatchError",
      `Mapped ${pack} item violates invariant '${invariant}'${ctxSuffix(context)}`,
      pack,
      context,
    );
    this.invariant = invariant;
  }
}
